import { reactive } from 'vue'
import mitt from 'mitt'
import { getEventKey, getEventValues } from '../utils/index'
// const emitter = mitt()
import type { ComponentRegisterInfo, ComponentOperationData } from '../types/index'
import { cloneDeep, forEach, isNull, isUndefined } from 'lodash-es'

/**
 * 事件中心，单例
 */
class EventCenter {
  private emitter
  // private allReceiveEvents: WidgetEvent[] = [] // 保存该页面下所有的接收事件
  private receiveComponentMap = new Map<string, ComponentRegisterInfo>()
  private eventValuePool: MutualPool // 每个组件的值缓存

  constructor() {
    this.emitter = mitt()
    this.eventValuePool = reactive<MutualPool>({})
  }

  get _eventValuePool() {
    return this.eventValuePool
  }
  clear() {
    this.emitter.all.clear()
    // this.allReceiveEvents = []
    this.receiveComponentMap.clear()
    // TODO: 暂时屏蔽主动清空，避免快速切换页面时，找不到参数查询报错问题
    // this.clearEventValuePool()
  }

  clearEmitter() {
    this.emitter.all.clear()
  }

  clearEventValuePool() {
    Object.keys(this.eventValuePool).forEach((key: any) => {
      delete this.eventValuePool[key]
    })
  }
  /**
   * 根据接收事件获得过滤值
   * @param receiveEvents 接收事件
   * @returns 交互值
   */
  getEventValuesByReceiveEvent(receiveEvents: WidgetEvent[]) {
    return getEventValues(this.eventValuePool, receiveEvents)
  }
  // 存储该页面所有的接收事件
  // setReceiveEvents(events: WidgetEvent[]) {
  //   this.allReceiveEvents = events
  // }
  /**
   * 初始化数据池
   * @param sendEvents 发送事件
   * @param constrainStatusOk 强制交互池中的数据状态为ok
   */
  initMutualPool(sendEvents: WidgetEvent[], constrainStatusOk = false) {
    forEach(sendEvents, (e: WidgetEvent) => {
      this.eventValuePool[getEventKey(e.key, e.currentPageId)] = {
        key: e.key,
        value: '',
        // 不是立即触发得，则默认都是ok
        status: e.action === 'promptlyFilters' && !constrainStatusOk ? 'waiting' : 'ok',
        sendEvent: e,
        updateTime: new Date().getTime()
      }
    })
  }

  rebuildSendEventInfo(sendEvents: WidgetEvent[]) {
    sendEvents.forEach((e: WidgetEvent) => {
      // 获得注册组件信息
      const componentInfo = this.receiveComponentMap.get(getEventKey(e.widgetKey!, e.currentPageId))
      if (componentInfo) {
        let val = ''
        if (componentInfo) {
          val = componentInfo.getEventValueCanUseObj()[e.bindValue!] || ''
        }
        this.eventValuePool[getEventKey(e.key, e.currentPageId)] = {
          key: e.key,
          value: val,
          status: 'ok',
          sendEvent: e,
          updateTime: new Date().getTime()
        }
      }
    })
  }

  deleteMutualPoolByKey(key: string, pageId = '') {
    delete this.eventValuePool[getEventKey(key, pageId)]
  }

  registerByComponent(component: ComponentRegisterInfo) {
    const _component = this.receiveComponentMap.get(component.id) as ComponentRegisterInfo
    if (_component) {
      // 重新设置部分信息
      _component.groupOp = component.groupOp
      _component.receiveEvents = component.receiveEvents
      // 进行重新注册
      this.register(_component, true)
    }
  }
  /**
   * 更新注册组件信息
   * @param component 组件
   * @param updateKeys 需要更新的属性
   */
  updateRegisterComponent(component: ComponentRegisterInfo, updateKeys: string[]) {
    const _component = this.receiveComponentMap.get(component.id) as ComponentRegisterInfo
    updateKeys.forEach((key: string) => (_component[key] = component[key]))
  }

  /**
   * 注册组件
   * @param component 组件信息
   * @param ignore 忽略组件注册这步
   */
  register(component: ComponentRegisterInfo, ignore = false) {
    // 注册组件信息
    if (!ignore) {
      this.receiveComponentMap.set(component.id, component)
      // 注册具有监听立即接收事件的方法，key: 组件ID_方法
      this.doImmediatelyOperation(component)
    }

    // 开启组件监听
    component.receiveEvents.forEach((receiveEvent: WidgetEvent) => {
      this.emitter.on(
        getEventKey(receiveEvent.receiveEventKey!, receiveEvent.currentPageId!),
        (payload: any) => {
          // 获得对应的组件
          const comp = this.receiveComponentMap.get(component.id)
          if (!comp) {
            return
          }
          if (receiveEvent.performOperation && receiveEvent.performOperation !== '') {
            this.checkAndExecute(comp, receiveEvent.performOperation)
          }
        }
      )
    })
    // console.log(
    //   '当前组件注册成功，当前组件信息，已注册组件数',
    //   component,
    //   this.receiveComponentMap.size
    // )
  }
  /**
   * 执行组件中监听接收事件为立即触发的事件
   * @param comp 组件信息
   */
  private doImmediatelyOperation(comp: ComponentRegisterInfo) {
    Object.keys(comp.groupOp).forEach((operation: string) => {
      const { widgetId, currentPageId, needJudgePromptly } = comp.groupOp[operation]
      if (needJudgePromptly) {
        // 获得对应的组件
        const comp = this.receiveComponentMap.get(getEventKey(widgetId, currentPageId))
        this.checkAndExecute(comp!, operation)
      }
    })
  }
  /**
   * 检查组件接收事件数据是否已准备好，并执行对应方法
   * @param comp 组件信息
   * @param performOperation 组件中接收到事件后，需要执行的方法
   */
  private checkAndExecute(comp: ComponentRegisterInfo, performOperation: string) {
    // 获得该接收事件所要执行的方法组
    const opInfo = comp.groupOp[performOperation]
    // 判断所有监听的数据是否都已经准备好
    let ready = true
    // 存放组件所监听的事件值
    const eventValues: MutualPool = {}
    if (opInfo) {
      opInfo.receiveEvent.forEach((e: WidgetEvent) => {
        const pool = this.eventValuePool[getEventKey(e.receiveEventKey!, e.currentPageId)]
        if (pool && pool !== null && pool.status === 'ok') {
          eventValues[e.receiveEventKey!] = cloneDeep(pool)
        } else if (!isUndefined(pool) && !isNull(pool)) {
          ready = false
        }
      })
    }

    // 所有数据都准备好了，则执行对应方法
    if (ready) {
      // 执行组件公共事件的方法
      comp?.onOperation!(performOperation, {
        data: eventValues,
        // reviceBindValue: receiveBindValue,
        receiveEvents: opInfo ? opInfo.receiveEvent : []
      })
    }
  }
  /**
   * 用isReady替代了
   * @param comp 组件信息
   */
  private doLoadDataPerformOperation(comp: ComponentRegisterInfo) {
    const loadOperationKey = 'reloadByEvent'
    const reloadOperationInfo = comp.groupOp[loadOperationKey]
    if (!reloadOperationInfo || !reloadOperationInfo.needJudgePromptly) {
      // 统一执行组件本身的加载方法
      this.checkAndExecute(comp, loadOperationKey)
    }
  }
  /**
   * 执行发送事件
   * @param sendEvent 发送事件集合
   */
  emitEvent(sendEvent: WidgetEvent, val: any) {
    // updateMutualPool(this.eventValuePool, sendEvent.key, val)
    this.emitter.emit(getEventKey(sendEvent.key, sendEvent.currentPageId), val)
  }
  /**
   * 更新交互池中的值
   * @param sendKey 发送事件KEY
   * @param value 交互值
   * @param status 指定交互值状态
   */
  updateMutualPool(sendKey: string, value: any, pageId: string, status: MutualPoolDataType = 'ok') {
    const _key = getEventKey(sendKey, pageId)
    if (this.eventValuePool[_key]) {
      this.eventValuePool[_key].status = status
      this.eventValuePool[_key].value = value
      this.eventValuePool[_key].updateTime = new Date().getTime()
    }
    console.log('updateMutualPool', cloneDeep(this.eventValuePool))
  }
  clearEventInfo(receiveEvents: WidgetEvent[]) {
    receiveEvents.forEach((e: WidgetEvent) => {
      // 清除交互池中的值
      delete this.eventValuePool[getEventKey(e.receiveEventKey!, e.currentPageId)]
      // 清除监听
      this.emitter.off(getEventKey(e.receiveEventKey!, e.currentPageId))
    })
  }
}

export const eventCenter = new EventCenter()
